import { CompositionContext, CompositionEngine, Container, PLATFORM, autoinject, transient } from "aurelia-framework";
import { StepModel } from "../../../../models/basics/basics.models";
import { I18N } from "aurelia-i18n";
import { VetActivities } from "./vet-activity/vet-activity";
import { DayPartHelper } from "../../../../helpers/day-part.helper";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { PscActivities } from "./psc-activity/psc-activity";
import { FormsActivities } from "./forms-activity/forms-activity";
import { FbrActivities } from "./fbr-activity/fbr-activity";
import { IncidentActivities } from "./incident-activity/incident-activity";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { TasksStepsEnum, TasksConstants } from "../../../../models/tasks/task.model";
import { CoachmarkClientService } from "../../../../services/coachmark/coachmark-client.service";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { GhgVerificationActivities } from "./ghg-activity/ghg-activity";
import { CfrActivities } from "./cfr-activity/cfr-activity";
import { RightShipInspectionActivities } from "./rightship-inspection-activity";
import { CrewWelfareActivities } from "./crew-welfare-activity/crew-welfare-activity";
import { SafetyRatingReviewActivities } from "./safety-score-review-activity";
import { VoyageDataSubmissionActivities } from "./voyage-data-submission-activity";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { LookupValueRequest } from "../../../../models/lookups/lookup.models";
import * as Enumerable from "linq";
import { VettingType } from "../../../../models/vetting/vetting.models";
import { DueDiligenceActivity } from "./due-diligence-activity/due-diligence-activity";

@autoinject()
@transient()
export class Activities {

    public currentTab: StepModel;

    public currentStepKey: string = TasksConstants.TabsCacheKey;

    tabs: StepModel[];

    constructor(private i18N: I18N, private vetActivity: VetActivities, private dueDiligenceActivity: DueDiligenceActivity,
        private voyageDataSubmissionActivity: VoyageDataSubmissionActivities,
        private formsActivities: FormsActivities,
        private pscActivity: PscActivities,
        private fbrActivities: FbrActivities,
        private crewWelfareActivities: CrewWelfareActivities,
        private safetyScoreReviewActivities: SafetyRatingReviewActivities,
        private cfrActivities: CfrActivities,
        private incActivities: IncidentActivities,
        private userClientServiceer: UserClientService,
        private userClaimsService: UserClaimsService,
        private coachmarkClientService: CoachmarkClientService,
        private ghgVerificationActivities: GhgVerificationActivities,
        private rightshipInspectionActivities: RightShipInspectionActivities,
        private lookupClientService: LookupClientService,
        private compositionEngine: CompositionEngine,
        private container: Container) {
    }

    async activate() {
        await this.initializeTabs();
        if (window.history.state && Object.keys(window.history.state).indexOf(TasksConstants.historyTaskTabKey) > -1) {
            const tabListName = window.history.state[TasksConstants.historyTaskTabKey];
            let step: number = null;
            switch (tabListName) {
                case TasksConstants.FBRListName:
                    step = TasksStepsEnum.FBR;
                    break;
                case TasksConstants.CFRListName:
                    step = TasksStepsEnum.CFR;
                    break;
                case TasksConstants.PSCListName:
                    step = TasksStepsEnum.PSC;
                    break;
                case TasksConstants.FormsListName:
                    step = TasksStepsEnum.Forms;
                    break;
                case TasksConstants.CrewWelfareListName:
                    step = TasksStepsEnum.CrewWelfare;
                    break;
                case TasksConstants.INCListName:
                    step = TasksStepsEnum.INC;
                    break;
                case TasksConstants.VETListName:
                    step = TasksStepsEnum.VET;
                    break;
                case TasksConstants.DueDiligenceListName:
                    step = TasksStepsEnum.DueDiligence;
                    break;
                case TasksConstants.GHGListName:
                    step = TasksStepsEnum.GHG;
                    break;
                case TasksConstants.INSPListName:
                    step = TasksStepsEnum.INSP;
                    break;
                case TasksConstants.SafetyRatingReviewListName:
                    step = TasksStepsEnum.Review;
                    break;
                case TasksConstants.VoyageDataSubmissionListName:
                    step = TasksStepsEnum.VoyageDataSubmission;
                    break;
                case TasksConstants.AutoScreening:
                    step = TasksStepsEnum.AutoScreening;
                    break;
            }

            delete window.history.state[TasksConstants.historyTaskTabKey];
            window.history.replaceState(window.history.state, "Activities");

            if (step != null) {
                const tabs = this.tabs.filter(x => x.step == step);
                if (tabs && tabs.length > 0)
                    this.currentTab = tabs[0];
            }
        }
    }

    attached() {
        this.coachmarkClientService.start(RightShipQiRouterConstants.RouteNames.Activities);
    }
    
    async initializeTabs()
    {
        const tabs = [
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessVETs),
                title: this.i18N.tr("vet-text"),
                step: TasksStepsEnum.VET,
                vm: this.vetActivity,
                class: "activities-vet",
                orderNumber: 1
            },
            {
                show: () => this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence),
                title: "Due Diligence",
                step: TasksStepsEnum.DueDiligence,
                vm: this.dueDiligenceActivity,
                class: "activities-due-diligence",
                orderNumber: 2
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessPSCs),
                title: this.i18N.tr("psc-text"),
                step: TasksStepsEnum.PSC,
                vm: this.pscActivity,
                orderNumber: 20
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessINCs),
                title: this.i18N.tr("inc-text"),
                step: TasksStepsEnum.INC,
                vm: this.incActivities,
                orderNumber: 25
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessFBRs),
                title: this.i18N.tr("fbr-text"),
                step: TasksStepsEnum.FBR,
                vm: this.fbrActivities,
                orderNumber: 30
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessCFRs),
                title: this.i18N.tr("cfr-text"),
                step: TasksStepsEnum.CFR,
                vm: this.cfrActivities,
                orderNumber: 35
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessForms),
                title: this.i18N.tr("forms-text"),
                step: TasksStepsEnum.Forms,
                vm: this.formsActivities,
                orderNumber: 40
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessEnvironmentals),
                title: this.i18N.tr("environment"),
                step: TasksStepsEnum.GHG,
                vm: this.ghgVerificationActivities,
                orderNumber: 45
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessRightShipInspections) || this.userClaimsService.hasPermission(Claims.CanAccessCargillInspections) || this.userClaimsService.hasPermission(Claims.CanAccessWetInspections),
                title: this.i18N.tr("insp"),
                step: TasksStepsEnum.INSP,
                vm: this.rightshipInspectionActivities,
                orderNumber: 55
            },{
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessCrewWelfare),
                title: this.i18N.tr("crew-welfare-text"),
                step: TasksStepsEnum.CrewWelfare,
                vm: this.crewWelfareActivities,
                orderNumber: 60
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessSafetyRatingReviews),
                title: this.i18N.tr("review-text"),
                step: TasksStepsEnum.Review,
                vm: this.safetyScoreReviewActivities,
                orderNumber: 65
            },
            {
                show: () => this.userClaimsService.hasPermission(Claims.CanAccessCarbonAccounting),
                title: this.i18N.tr("carbon-accounting"),
                step: TasksStepsEnum.VoyageDataSubmission,
                vm: this.voyageDataSubmissionActivity,
                orderNumber: 70
            }
        ];

        const ruleCollectionTabs = await this.loadRuleCollectionTabs();
        tabs.push(...ruleCollectionTabs);

        this.tabs = Enumerable.from(tabs).orderBy(x => x.orderNumber).toArray();
    }

    private async loadRuleCollectionTabs() {
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RuleCollectionUIListTemplates;
        const response = await this.lookupClientService.getLookup(request);
        const ruleCollectionTabs = response.activeValues.map(v => {
            return {
                id: v.id,
                name: v.name,
                internalName: v.additionalData.internalName,
                perminssIonName: v.additionalData.perminssIonName,
                orderNumber: v.additionalData.orderNumber,
                vettingTypeId: v.additionalData.vettingTypeId
            };
        });

        const tabs: any[] = [];

        for(const rc of ruleCollectionTabs)
        {
            const defaultActivty = rc.vettingTypeId == VettingType.AutoScreening? "/screening-activity": "/vet-activity";
            const activty = "/" + rc.internalName + "-activity";

            const tab = {
                show: () => rc.perminssIonName? this.userClaimsService.hasPermission(rc.perminssIonName): true,
                title: rc.name,
                step: TasksStepsEnum.AutoScreening,
                orderNumber: rc.orderNumber,
                getModel: () => {
                    return {
                        listTemplateId: rc.id,
                        inlcudeNullTemplates: defaultActivty == activty
                    }
                },
                onClick: async (tabsVM, clickedTab) => {
                    //if already loaded, return
                    if(clickedTab.vm != null) return;

                    let obj = null; 
                    const basePath = "views/pages/admin/activities"
                    const container = rc.vettingTypeId == VettingType.AutoScreening? "/screening-activities": "";

                    try {
                        const vmPath = basePath + "/" + (container?.startsWith('/')? container.substring(1): container) + "/" + (activty?.startsWith('/')? activty.substring(1): activty) + "/"+ (activty?.startsWith('/')? activty.substring(1): activty);
                        require(vmPath + ".ts")
                        require(vmPath + ".html")

                        obj = await this.compositionEngine.createController(<CompositionContext>{ viewModel: PLATFORM.moduleName(vmPath), bindingContext: this, container: this.container });
                    } 
                    catch (e) {
                        obj = await this.compositionEngine.createController(<CompositionContext>{ viewModel: PLATFORM.moduleName(basePath + container + defaultActivty + defaultActivty), bindingContext: this, container: this.container});
                    }
                    const vm = obj.viewModel;
                    clickedTab.vm = vm;
                }
            };

            tabs.push(tab);
        }

        return tabs;
    }

    public getTitle() {
        return DayPartHelper.getCurrentDayPart() + ' ' + this.userClientServiceer.currentUser.userName;
    }
}
